import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useAppFeatures } from './AppFeaturesContext';
import authService from '../services/auth.service';
import { getVersionOutdated } from '../utils/versionOutdated';
import { useNavigate } from 'react-router-dom';
import { showToast } from '../utils/toast';
import statsWebSocketService from '../services/statsWebSocket.service';

// --- Типы ---

type ActiveTicketsCounts = {
  total?: number;
  by_stage?: Record<string, number>;
};

type MyTasksCounts = {
  total?: number;
  overdue?: number;
  today?: number;
  tomorrow?: number;
  day_after_tomorrow?: number;
  later?: number;
};

type NewTicketsCounts = {
  total?: number;
  today?: number;
  yesterday?: number;
  day_before_yesterday?: number;
  earlier?: number;
};

type LeadsStats = {
  new_leads: { count: number };
  assigned_leads: { count: number };
  my_leads: { count: number };
  all_leads: { count: number };
};

// Интерфейсы хуков (обратная совместимость 1:1)
interface ActiveTicketsStatsValue {
  counts: ActiveTicketsCounts;
  connected: boolean;
}

interface MyAssignedStatsValue {
  counts: MyTasksCounts;
  connected: boolean;
}

interface NewTicketsStatsValue {
  counts: NewTicketsCounts;
  connected: boolean;
}

interface LeadsStatsContextValue {
  stats: LeadsStats;
  connected: boolean;
}

// Внутренний контекст
interface UnifiedStatsContextValue {
  activeTickets: ActiveTicketsCounts;
  myTasks: MyTasksCounts;
  newTickets: NewTicketsCounts;
  leadsStats: LeadsStats;
  connected: boolean;
}

const DEFAULT_LEADS: LeadsStats = {
  new_leads: { count: 0 },
  assigned_leads: { count: 0 },
  my_leads: { count: 0 },
  all_leads: { count: 0 },
};

const INTERVAL = 15;

const UnifiedStatsContext = createContext<UnifiedStatsContextValue | undefined>(undefined);

export const UnifiedStatsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [activeTickets, setActiveTickets] = useState<ActiveTicketsCounts>({});
  const [myTasks, setMyTasks] = useState<MyTasksCounts>({});
  const [newTickets, setNewTickets] = useState<NewTicketsCounts>({});
  const [leadsStats, setLeadsStats] = useState<LeadsStats>(DEFAULT_LEADS);
  const [connected, setConnected] = useState(false);

  const { tickets, leads } = useAppFeatures();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Вычисляем типы на основе включённых фич
  const types = useMemo(() => {
    const t: string[] = [];
    if (tickets) t.push('tickets_active', 'tickets_tasks', 'tickets_new');
    if (leads) t.push('leads');
    return t;
  }, [tickets, leads]);

  // Стабильная ссылка на types для callback-ов
  const typesRef = useRef(types);
  typesRef.current = types;

  // Обработчик статистики
  const handleStats = useCallback((data: Record<string, unknown>) => {
    const ticketsData = (data.tickets && typeof data.tickets === 'object')
      ? (data.tickets as Record<string, unknown>)
      : data;

    // Tickets: active_tickets
    if (ticketsData.active_tickets && typeof ticketsData.active_tickets === 'object') {
      const d = ticketsData.active_tickets as Record<string, unknown>;
      const total = Number(d.total ?? 0);
      const by_stage = (d.by_stage && typeof d.by_stage === 'object') ? d.by_stage as Record<string, number> : undefined;
      setActiveTickets({ total: Number.isFinite(total) ? total : 0, by_stage });
    }

    // Tickets: my_tasks
    if (ticketsData.my_tasks && typeof ticketsData.my_tasks === 'object') {
      const d = ticketsData.my_tasks as Record<string, unknown>;
      setMyTasks({
        total: Number(d.total ?? 0),
        overdue: Number(d.overdue ?? 0),
        today: Number(d.today ?? 0),
        tomorrow: Number(d.tomorrow ?? 0),
        day_after_tomorrow: Number(d.day_after_tomorrow ?? 0),
        later: Number(d.later ?? 0),
      });
    }

    // Tickets: new_tickets
    if (ticketsData.new_tickets && typeof ticketsData.new_tickets === 'object') {
      const d = ticketsData.new_tickets as Record<string, unknown>;
      setNewTickets({
        total: Number(d.total ?? 0),
        today: Number(d.today ?? 0),
        yesterday: Number(d.yesterday ?? 0),
        day_before_yesterday: Number(d.day_before_yesterday ?? 0),
        earlier: Number(d.earlier ?? 0),
      });
    }

    // Leads
    if (data.leads && typeof data.leads === 'object') {
      const s = data.leads as Record<string, { count?: number }>;
      setLeadsStats({
        new_leads: { count: Number(s.new_leads?.count ?? 0) },
        assigned_leads: { count: Number(s.assigned_leads?.count ?? 0) },
        my_leads: { count: Number(s.my_leads?.count ?? 0) },
        all_leads: { count: Number(s.all_leads?.count ?? 0) },
      });
    }
  }, []);

  // Подключение / отключение WS при изменении types
  useEffect(() => {
    if (types.length === 0) {
      statsWebSocketService.disconnect();
      setConnected(false);
      setActiveTickets({});
      setMyTasks({});
      setNewTickets({});
      setLeadsStats(DEFAULT_LEADS);
      return;
    }

    // Не подключаемся если версия устарела или нет токена
    if (getVersionOutdated()) return;
    const tokens = authService.getTokens();
    if (!tokens?.access_token) return;

    // Подписки
    const unsubStats = statsWebSocketService.onStats(handleStats);
    const unsubConn = statsWebSocketService.onConnectionChange(setConnected);
    const unsubSession = statsWebSocketService.onSessionEvent((event) => {
      if (event === 'session_invalidated') {
        showToast('Ваша сессия была завершена', 'warning');
        navigateRef.current('/login');
      }
    });

    statsWebSocketService.connect(types, INTERVAL);

    // Reconnect при возвращении на вкладку
    const handleVisibility = () => {
      if (!document.hidden && !statsWebSocketService.isConnected()) {
        if (!getVersionOutdated() && authService.getTokens()?.access_token) {
          statsWebSocketService.connect(typesRef.current, INTERVAL);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      unsubStats();
      unsubConn();
      unsubSession();
      statsWebSocketService.disconnect();
    };
  }, [types, handleStats]);

  const value = useMemo<UnifiedStatsContextValue>(
    () => ({ activeTickets, myTasks, newTickets, leadsStats, connected }),
    [activeTickets, myTasks, newTickets, leadsStats, connected]
  );

  return <UnifiedStatsContext.Provider value={value}>{children}</UnifiedStatsContext.Provider>;
};

// --- Обратно-совместимые хуки ---

export const useActiveTicketsStats = (): ActiveTicketsStatsValue => {
  const ctx = useContext(UnifiedStatsContext);
  if (!ctx) throw new Error('useActiveTicketsStats must be used within UnifiedStatsProvider');
  return { counts: ctx.activeTickets, connected: ctx.connected };
};

export const useMyAssignedStats = (): MyAssignedStatsValue => {
  const ctx = useContext(UnifiedStatsContext);
  if (!ctx) throw new Error('useMyAssignedStats must be used within UnifiedStatsProvider');
  return { counts: ctx.myTasks, connected: ctx.connected };
};

export const useNewTicketsStats = (): NewTicketsStatsValue => {
  const ctx = useContext(UnifiedStatsContext);
  if (!ctx) throw new Error('useNewTicketsStats must be used within UnifiedStatsProvider');
  return { counts: ctx.newTickets, connected: ctx.connected };
};

export const useLeadsStats = (): LeadsStatsContextValue => {
  const ctx = useContext(UnifiedStatsContext);
  if (!ctx) throw new Error('useLeadsStats must be used within UnifiedStatsProvider');
  return { stats: ctx.leadsStats, connected: ctx.connected };
};
